/**
 * MembershipsManagementTab.js
 * Admin component for configuring Professional and Premium membership plan pricing.
 * Restricted to ADMIN and SUPER_ADMIN roles.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { vendorService } from '../../../services/api';
import useAuth from '../../../hooks/useAuth';
import {
  Settings, RefreshCw, Check, Loader, AlertCircle, ShieldAlert, CreditCard
} from 'lucide-react';

const MembershipsManagementTab = ({ toast }) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole && hasRole('ADMIN');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [form, setForm] = useState({
    professional_price: 12000,
    premium_price: 25000,
    professional_plan_id: 'plan_TabHs7Uuj3A13W',
    premium_plan_id: 'plan_TabLDPpcFqUt7Z',
  });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getAdminMembershipConfig();
      if (res.success && res.data) {
        setForm({
          professional_price: res.data.professional_price,
          premium_price: res.data.premium_price,
          professional_plan_id: res.data.professional_plan_id || 'plan_TabHs7Uuj3A13W',
          premium_plan_id: res.data.premium_plan_id || 'plan_TabLDPpcFqUt7Z',
        });
      } else {
        toast.error(res.error || 'Failed to load membership pricing configuration.');
      }
    } catch (err) {
      console.error('Error fetching membership config:', err);
      toast.error(err?.message || err?.detail || 'Failed to load membership configuration.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchConfig();
    }
  }, [isAdmin, fetchConfig]);

  const handleSave = async () => {
    if (form.professional_price <= 0 || form.premium_price <= 0) {
      toast.error('Prices must be positive numbers greater than 0.');
      return;
    }
    if (!form.professional_plan_id?.trim() || !form.premium_plan_id?.trim()) {
      toast.error('Razorpay AutoPay Plan IDs cannot be empty.');
      return;
    }

    setSaving(true);
    setSavedOk(false);
    try {
      const res = await vendorService.updateAdminMembershipConfig({
        professional_price: Number(form.professional_price),
        premium_price: Number(form.premium_price),
        professional_plan_id: form.professional_plan_id.trim(),
        premium_plan_id: form.premium_plan_id.trim(),
      });

      if (res.success) {
        toast.success(res.message || 'Membership plan configuration updated successfully!');
        setSavedOk(true);
        if (res.data?.config) {
          setForm({
            professional_price: res.data.config.professional_price,
            premium_price: res.data.config.premium_price,
            professional_plan_id: res.data.config.professional_plan_id,
            premium_plan_id: res.data.config.premium_plan_id,
          });
        }
        setTimeout(() => setSavedOk(false), 3000);
      } else {
        toast.error(res.error || res.message || 'Failed to update membership configuration.');
      }
    } catch (err) {
      console.error('Error updating membership config:', err);
      toast.error(err?.message || err?.detail || 'Failed to update membership configuration.');
    } finally {
      setSaving(false);
    }
  };

  // Role Access Guard: Only ADMIN or SUPER_ADMIN
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 border border-red-200 rounded-2xl bg-red-50/50 p-6 text-center">
        <ShieldAlert size={36} className="text-red-500" />
        <div>
          <h3 className="text-base font-bold text-gray-800">Access Denied</h3>
          <p className="text-xs text-gray-500 mt-1 max-w-md">
            Only Admin and Super Admin roles have permission to view or manage Membership Plan Pricing configurations.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <RefreshCw className="animate-spin text-[#2ab391]" size={30} />
        <span className="text-sm font-semibold text-gray-500">Loading Membership Configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <div>
        <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <Settings size={18} className="text-[#2ab391]" />
          Memberships Management
        </h3>
        <p className="text-xs text-gray-400 mt-1 font-semibold">
          Configure annual membership pricing and Razorpay AutoPay Plan IDs for Professional and Premium vendor plans.
        </p>
      </div>

      <div className="max-w-xl bg-white rounded-2xl border border-gray-200/80 p-6 shadow-xs space-y-5">
        <h4 className="text-sm font-bold text-gray-700 border-b border-gray-100 pb-2 flex items-center gap-2">
          <CreditCard size={16} className="text-[#2ab391]" />
          Vendor Plan Pricing & AutoPay Mandate Setup
        </h4>

        {/* Professional Plan Price */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
            Professional Plan Price (₹ INR / year) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
            <input
              type="number"
              min={1}
              value={form.professional_price}
              onChange={(e) => setForm((f) => ({ ...f, professional_price: Number(e.target.value) }))}
              placeholder="e.g. 12000"
              className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
            />
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Current annual amount charged when vendors upgrade to Professional membership.
          </p>
        </div>

        {/* Professional AutoPay Plan ID */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
            Professional AutoPay Plan ID (Razorpay) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.professional_plan_id}
            onChange={(e) => setForm((f) => ({ ...f, professional_plan_id: e.target.value }))}
            placeholder="e.g. plan_TabHs7Uuj3A13W"
            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-mono focus:outline-none focus:border-[#2ab391] transition-all"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Razorpay Subscription Plan ID for Professional AutoPay mandate (`plan_...`).
          </p>
        </div>

        <hr className="border-gray-100" />

        {/* Premium Plan Price */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
            Premium Plan Price (₹ INR / year) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
            <input
              type="number"
              min={1}
              value={form.premium_price}
              onChange={(e) => setForm((f) => ({ ...f, premium_price: Number(e.target.value) }))}
              placeholder="e.g. 25000"
              className="w-full bg-white border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-gray-800 text-sm font-semibold focus:outline-none focus:border-[#2ab391] transition-all"
            />
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Current annual amount charged when vendors upgrade to Premium membership.
          </p>
        </div>

        {/* Premium AutoPay Plan ID */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5 font-bold uppercase tracking-wider">
            Premium AutoPay Plan ID (Razorpay) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.premium_plan_id}
            onChange={(e) => setForm((f) => ({ ...f, premium_plan_id: e.target.value }))}
            placeholder="e.g. plan_TabLDPpcFqUt7Z"
            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-gray-800 text-sm font-mono focus:outline-none focus:border-[#2ab391] transition-all"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Razorpay Subscription Plan ID for Premium AutoPay mandate (`plan_...`).
          </p>
        </div>

        {/* Action Button */}
        <button
          onClick={() => setShowConfirmModal(true)}
          disabled={saving}
          className={`mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-colors active:scale-[0.98] shadow-md shadow-[#2ab391]/10 cursor-pointer ${
            savedOk
              ? 'bg-emerald-500 text-white'
              : 'bg-[#2ab391] hover:bg-[#23977a] text-white disabled:opacity-60'
          }`}
        >
          {saving ? <Loader size={16} className="animate-spin" /> : savedOk ? <Check size={16} /> : null}
          {saving ? 'Saving...' : savedOk ? 'Saved Configuration!' : 'Save Config'}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 text-left space-y-4 transform transition-all">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                <AlertCircle className="text-amber-600" size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Confirm Membership Configuration Update</h3>
                <p className="text-xs text-gray-500 font-medium">Please review the updated plan pricing and AutoPay Plan IDs before saving.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3.5 space-y-2 text-xs border border-gray-150">
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Professional Price:</span>
                <span className="font-bold text-gray-900">₹{Number(form.professional_price).toLocaleString('en-IN')}/year</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Professional AutoPay ID:</span>
                <span className="font-mono font-bold text-gray-800">{form.professional_plan_id}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Premium Price:</span>
                <span className="font-bold text-gray-900">₹{Number(form.premium_price).toLocaleString('en-IN')}/year</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 font-semibold">Premium AutoPay ID:</span>
                <span className="font-mono font-bold text-gray-800">{form.premium_plan_id}</span>
              </div>
            </div>

            <p className="text-xs text-gray-600 font-medium leading-relaxed">
              Are you sure you want to save these updated membership configurations? New vendor AutoPay subscriptions will be created using these Plan IDs and prices.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={saving}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  handleSave();
                }}
                disabled={saving}
                className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white text-xs font-bold rounded-xl transition-colors shadow-xs cursor-pointer"
              >
                Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipsManagementTab;
